import React, { useContext, useEffect } from 'react';
import axios from 'axios';

import LoadingContext from '../contexts/loading';

const Interceptor = React.memo(() => {
  let requestCount = 0;
  let isRefreshing = false;
  let failedQueue = [];

  const { hideSpinner, showSpinner } = useContext(LoadingContext);

  const setSpinner = toShow => {
    if (toShow) {
      requestCount += 1;

      if (requestCount === 1) {
        showSpinner();
      }
    } else {
      requestCount -= 1;

      if (requestCount <= 0) {
        requestCount = 0;

        hideSpinner();
      }
    }
  };

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    failedQueue = [];
  };

  const logOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    localStorage.removeItem('agencyId');
  };

  const setupInterceptors = () => {
    axios.interceptors.request.use(
      request => {
        setSpinner(true);

        const token = localStorage.getItem('token');
        const agencyId = localStorage.getItem('agencyId');

        if (token) {
          request.headers['Authorization'] = `Bearer ${token}`;
        }

        if (agencyId) {
          request.headers['X-AgencyId'] = agencyId;
        }

        return request;
      },
      error => {
        setSpinner(false);

        return Promise.reject(error);
      },
    );

    axios.interceptors.response.use(
      response => {
        setSpinner(false);

        return response;
      },
      error => {
        const originalRequest = error.config;

        setSpinner(false);

        if (error && error.response) {
          if (error.response.status === 401) {
            if (originalRequest.url.endsWith('/refresh')) {
              logOut();

              window.location.href = '/login';

              return Promise.reject(error);
            }

            if (!originalRequest._retry) {
              // If refreshing the token then send request to a queue
              if (isRefreshing) {
                return new Promise((resolve, reject) => {
                  failedQueue.push({ resolve, reject });
                })
                  .then(() => {
                    originalRequest.headers[
                      'Authorization'
                    ] = `Bearer ${localStorage.getItem('token')}`;

                    return axios(originalRequest);
                  })
                  .catch(err => err);
              }

              // If header of the request has changed, it means token was refreshed
              if (
                originalRequest.headers['Authorization'] !==
                `Bearer ${localStorage.getItem('token')}`
              ) {
                originalRequest.headers[
                  'Authorization'
                ] = `Bearer ${localStorage.getItem('token')}`;

                return Promise.resolve(axios(originalRequest));
              }

              originalRequest._retry = true;
              isRefreshing = true;

              const refreshToken = localStorage.getItem('refresh');

              return new Promise((resolve, reject) => {
                axios
                  .post(
                    `${process.env.REACT_APP_BASE_URL}account/login/refresh`,
                    {
                      token: refreshToken,
                    },
                  )
                  .then(res => {
                    if (res.status === 200) {
                      isRefreshing = false;
                      // update token
                      localStorage.removeItem('token');
                      localStorage.removeItem('refresh');
                      localStorage.setItem('token', res.data.Token);
                      localStorage.setItem('refresh', res.data.RefreshToken);

                      // resolve queued
                      processQueue(null, res.data.Token);

                      // resolve current
                      originalRequest.headers.Authorization = `Bearer ${res.data.Token}`;
                      resolve(axios(originalRequest));
                    } else {
                      logOut();

                      window.location.href = '/login';
                      reject(error);
                    }
                  })
                  .catch(err => {
                    processQueue(err, null);
                    reject(err);

                    logOut();

                    window.location.href = '/login';
                  });
              });
            }
          }
        } else if (error.response.status === 503) {
          window.location.href = '/error-503';

          return Promise.reject(error);
        }

        this.showError(error);

        return Promise.reject(error);
      },
    );
  };

  useEffect(() => {
    setupInterceptors();
  }, []);

  return <></>;
});

Interceptor.propTypes = {};

export default Interceptor;
